import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import {
  fetchAllowedMenus,
  fetchLocationData,
  fetchProjectEditData,
  updateProject,
  fetchUserData,
  assignTargetToUsers,
  AssignUser,
  fetchAllAssigedObjects,
} from "../Services/APIService";
import Select from "react-select";
import { ActionMeta, MultiValue } from "react-select";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface ValidationErrors {
  projectName?: string;
  description?: string;
  locationID?: string;
  contactMails?: string;
  assignmentType?: string;
  assignmentTypeId?: string;
  listOfUsers?: string;
}

interface Location {
  locationID: number;
  locationName: string;
}

interface Project {
  projectID: number;
  projectName: string;
  description: string;
  locationID: number;
  contactMails: string;
  brochure: string;
  priceQuote: string;
  tenantId: number;
  isActive: boolean;
}

interface AssignmentData {
  tenantId: number;
  assignmentTypeId: number;
  assignmentObjectId: number;
  isAssigned: boolean;
  listOfUsers: {
    userId: number;
  }[];
  createdBy: number;
}

interface AssignmentUser {
  userId: number;
  isActive: boolean;
}

interface AssignmentDatas {
  tenantId: number;
  assignmentTypeId: number;
  assignmentObjectId: number;
  isAssigned: boolean;
  listOfUsers: AssignmentUser[];
  createdBy: number;
}

interface User {
  userId: number;
  userName: string;
  email: string;
}

interface SelectOption {
  value: number;
  label: string;
  userData: User;
}

const EditProject: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [locations, setLocations] = useState<Location[]>([]);

  // State for project data
  const [projectData, setProjectData] = useState<Project | null>(null);

  // State for contact emails input
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);

  // State for file uploads
  const [brochureFile, setBrochureFile] = useState<File | null>(null);
  const [priceQuoteFile, setPriceQuoteFile] = useState<File | null>(null);

  // State to track if files have been changed
  const [brochureChanged, setBrochureChanged] = useState(false);
  const [priceQuoteChanged, setPriceQuoteChanged] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const [assignmentData, setAssignmentData] = useState<AssignmentDatas>({
    tenantId: 0,
    assignmentTypeId: 2,
    assignmentObjectId: 0,
    isAssigned: true,
    listOfUsers: [],
    createdBy: 0,
  });

  // Add these state variables to your EditProject component
  const [users, setUsers] = useState<User[]>([]);
  const [userOptions, setUserOptions] = useState<SelectOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<SelectOption[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const [isLoadingAssignedUsers, setIsLoadingAssignedUsers] = useState(false);
  const [initialAssignedUsers, setInitialAssignedUsers] = useState<number[]>(
    []
  );
  const [assignedUsers, setAssignedUsers] = useState<AssignUser[]>([]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Fetch menus and locations
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [menus, locationData, usersData] = await Promise.all([
          fetchAllowedMenus(),
          fetchLocationData(),
          fetchUserData(),
        ]);

        setAllowedMenus(menus);
        setLocations(locationData);

        setUsers(usersData);
        const options = usersData.map((user) => ({
          value: user.userId,
          label: `${user.userName} (${user.email})`,
          userData: user,
        }));
        setUserOptions(options);

        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load required data");
      } finally {
        setIsLoading(false);
        setIsLoadingUsers(false);
      }
    };

    fetchData();
  }, []);

  // Load project data from navigation state and assigned users
  useEffect(() => {
    const loadProjectData = async () => {
      try {
        const projectInfo = location.state?.projectData;
        console.log("Project Info:", projectInfo);
        if (!projectInfo) {
          const urlParams = new URLSearchParams(location.search);
          const projectID = parseInt(urlParams.get("projectID") || "0");
          const locationID = parseInt(urlParams.get("locationID") || "0");

          if (projectID && locationID) {
            const data = await fetchProjectEditData(projectID, locationID);
            setProjectData(data);

            setAssignmentData((prev) => ({
              ...prev,
              assignmentObjectId: projectID,
            }));

            setIsLoadingAssignedUsers(true);
            const fetchedAssignedUsers: AssignUser[] =
              await fetchAllAssigedObjects(projectID, 2);
            // Transform AssignUser[] to AssignmentUser[] by adding isActive
            const assignedUsersData: AssignmentUser[] =
              fetchedAssignedUsers.map((user) => ({
                userId: user.userId,
                isActive: true, // Initially set all fetched users as active
              }));

            const options =
              userOptions.length > 0
                ? userOptions
                : (await fetchUserData()).map((user) => ({
                    value: user.userId,
                    label: `${user.userName} (${user.email})`,
                    userData: user,
                  }));
            const preSelected = options.filter((option) =>
              assignedUsersData.some(
                (assignedUser) => assignedUser.userId === option.value
              )
            );
            setSelectedUsers(preSelected);

            const initialUserIds = assignedUsersData.map((user) => user.userId);
            setInitialAssignedUsers(initialUserIds);

            setAssignmentData((prev) => ({
              ...prev,
              listOfUsers: assignedUsersData,
            }));

            if (data.contactMails) {
              setEmails(
                data.contactMails
                  .split(",")
                  .map((email: string) => email.trim())
              );
            }
          } else {
            toast.error("Missing project information");
            navigate("/projectView");
          }
        } else {
          setProjectData(projectInfo);

          setAssignmentData((prev) => ({
            ...prev,
            assignmentObjectId: projectInfo.projectID,
          }));

          setIsLoadingAssignedUsers(true);
          const fetchedAssignedUsers: AssignUser[] =
            await fetchAllAssigedObjects(2, projectInfo.projectID);
          // Transform AssignUser[] to AssignmentUser[] by adding isActive
          const assignedUsersData: AssignmentUser[] = fetchedAssignedUsers.map(
            (user) => ({
              userId: user.userId,
              isActive: true, // Initially set all fetched users as active
            })
          );

          const options =
            userOptions.length > 0
              ? userOptions
              : (await fetchUserData()).map((user) => ({
                  value: user.userId,
                  label: `${user.userName} (${user.email})`,
                  userData: user,
                }));
          const preSelected = options.filter((option) =>
            assignedUsersData.some(
              (assignedUser) => assignedUser.userId === option.value
            )
          );
          setSelectedUsers(preSelected);

          const initialUserIds = assignedUsersData.map((user) => user.userId);
          setInitialAssignedUsers(initialUserIds);

          setAssignmentData((prev) => ({
            ...prev,
            listOfUsers: assignedUsersData,
          }));

          if (projectInfo.contactMails) {
            setEmails(
              projectInfo.contactMails
                .split(",")
                .map((email: string) => email.trim())
            );
          }

          // Handle fetchAllAssigUsers with error handling
          try {
            const assignUsers = await fetchAllAssigedObjects(
              2,
              projectInfo.projectID
            );
            setAssignedUsers(assignUsers);
          } catch (err) {
            console.error("Failed to fetch assigned users:", err);
            setAssignedUsers([]);
            toast.error(
              "Failed to fetch assigned users. Proceeding without user data."
            );
          }
        }
      } catch (error) {
        console.error("Error loading project data:", error);
        toast.error("Failed to load project data");
        navigate("/projectView");
      } finally {
        setIsLoadingAssignedUsers(false);
      }
    };

    loadProjectData();
  }, [location, navigate, userOptions]);

  // Update tenantId and createdBy from localStorage when component mounts
  useEffect(() => {
    const tenantId = localStorage.getItem("tenantId");
    const userId = localStorage.getItem("UserId");

    setAssignmentData((prev) => ({
      ...prev,
      tenantId: tenantId ? parseInt(tenantId) : 0,
      createdBy: userId ? parseInt(userId) : 0,
    }));
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setProjectData((prev) => (prev ? { ...prev, [name]: checked } : null));
      return;
    }

    if (value.startsWith(" ")) {
      return;
    }

    setProjectData((prev) => (prev ? { ...prev, [name]: value } : null));

    if (errors[name as keyof ValidationErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(e.target.value);
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addEmail();
    }
  };

  const addEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (trimmedEmail && validateEmail(trimmedEmail)) {
      if (!emails.includes(trimmedEmail)) {
        setEmails([...emails, trimmedEmail]);
        if (projectData) {
          setProjectData({
            ...projectData,
            contactMails: [...emails, trimmedEmail].join(","),
          });
        }
        setEmailInput("");
        if (errors.contactMails) {
          setErrors({
            ...errors,
            contactMails: undefined,
          });
        }
      }
    } else if (trimmedEmail) {
      setErrors({
        ...errors,
        contactMails: "Invalid email format",
      });
    }
  };

  const removeEmail = (emailToRemove: string) => {
    const updatedEmails = emails.filter((email) => email !== emailToRemove);
    setEmails(updatedEmails);
    if (projectData) {
      setProjectData({
        ...projectData,
        contactMails: updatedEmails.join(","),
      });
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: "brochure" | "priceQuote"
  ) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      if (fileType === "brochure") {
        setBrochureFile(file);
        setBrochureChanged(true);
      } else {
        setPriceQuoteFile(file);
        setPriceQuoteChanged(true);
      }
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        const base64Content = base64String.split(",")[1];
        resolve(base64Content);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!projectData?.projectName.trim()) {
      newErrors.projectName = "Project name is required";
      isValid = false;
    }

    if (!projectData?.locationID) {
      newErrors.locationID = "Location is required";
      isValid = false;
    }

    if (!projectData?.description.trim()) {
      newErrors.description = "Description is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateAssignmentData = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    // if (!assignmentData.assignmentTypeId) {
    //   newErrors.assignmentTypeId = "Assignment type is required";
    //   isValid = false;
    // }

    if (!selectedUsers || selectedUsers.length === 0) {
      newErrors.listOfUsers = "Please select at least one user";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleMultiSelectChange = (
    newValue: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    if (!newValue || newValue.length === 0) {
      setSelectedUsers([]);
      const updatedList = assignmentData.listOfUsers.map((user) => ({
        userId: user.userId,
        isActive: false,
      }));
      setAssignmentData({
        ...assignmentData,
        listOfUsers: updatedList,
      });
      return;
    }

    const selectedOptions = Array.isArray(newValue) ? newValue : [newValue];
    setSelectedUsers(selectedOptions);

    const selectedUserIds = selectedOptions.map((option) => option.value);

    const updatedListOfUsers: AssignmentUser[] = assignmentData.listOfUsers.map(
      (user) => ({
        userId: user.userId,
        isActive: selectedUserIds.includes(user.userId),
      })
    );

    setAssignmentData({
      ...assignmentData,
      listOfUsers: updatedListOfUsers,
    });

    if (errors.listOfUsers) {
      setErrors({
        ...errors,
        listOfUsers: undefined,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !projectData) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      let brochureData = projectData.brochure;
      let priceQuoteData = projectData.priceQuote;

      if (brochureChanged && brochureFile) {
        brochureData = await fileToBase64(brochureFile);
      }

      if (priceQuoteChanged && priceQuoteFile) {
        priceQuoteData = await fileToBase64(priceQuoteFile);
      }

      const userId = localStorage.getItem("UserId");
      const tenantId = localStorage.getItem("tenantId");

      const updateData = {
        projectID: projectData.projectID,
        projectName: projectData.projectName,
        description: projectData.description,
        locationID: projectData.locationID,
        isActive: projectData.isActive,
        contactMails: emails.join(","),
        brochure: brochureData,
        priceQuote: priceQuoteData,
        tenantId: tenantId ? parseInt(tenantId) : 0,
        modifieddBy: userId ? parseInt(userId) : 0,
      };

      const result = await updateProject(updateData);

      if (result.isUpdated) {
        toast.success(result.message || "Project updated successfully");
        setTimeout(() => {
          navigate("/projectView");
        }, 1000);
      } else {
        toast.error(result.message || "Failed to update project");
      }
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error("An error occurred while updating the project");
    }
  };

  const handleAssignmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateAssignmentData()) {
      toast.error("Please select at least one user.");
      return;
    }

    try {
      setIsSubmittingAssignment(true);
      setIsLoadingAssignedUsers(true);

      const currentSelectedUserIds = selectedUsers.map((user) => user.value);

      const usersToUnassign = initialAssignedUsers.filter(
        (userId) => !currentSelectedUserIds.includes(userId)
      );

      // Assign ALL currently selected users, not just new ones
      const usersToAssign = currentSelectedUserIds;

      // Perform unassign operation if there are users to unassign
      if (usersToUnassign.length > 0) {
        const unassignData: AssignmentData = {
          tenantId: assignmentData.tenantId,
          assignmentTypeId: assignmentData.assignmentTypeId,
          assignmentObjectId: assignmentData.assignmentObjectId,
          isAssigned: false,
          listOfUsers: usersToUnassign.map((userId) => ({ userId })),
          createdBy: assignmentData.createdBy,
        };

        const unassignResult = await assignTargetToUsers(unassignData);
        if (!unassignResult.isUpdated) {
          throw new Error(
            unassignResult.message || "Failed to unassign users."
          );
        }
      }

      // Perform assign operation if there are users to assign
      if (usersToAssign.length > 0) {
        const assignData: AssignmentData = {
          tenantId: assignmentData.tenantId,
          assignmentTypeId: assignmentData.assignmentTypeId,
          assignmentObjectId: assignmentData.assignmentObjectId,
          isAssigned: true,
          listOfUsers: usersToAssign.map((userId) => ({ userId })),
          createdBy: assignmentData.createdBy,
        };

        const assignResult = await assignTargetToUsers(assignData);
        if (!assignResult.isUpdated) {
          throw new Error(assignResult.message || "Failed to assign users.");
        }

        // Success handling similar to handleSubmit
        toast.success(assignResult.message || "Users updated successfully");
      }

      if (usersToUnassign.length === 0 && usersToAssign.length === 0) {
        toast.error("No users selected to assign.");
        return;
      }

      // Fetch updated user data after successful operations
      let updatedUsersData: User[];
      let fetchedAssignedUsers: AssignUser[];
      try {
        [updatedUsersData, fetchedAssignedUsers] = await Promise.all([
          fetchUserData(),
          fetchAllAssigedObjects(2, assignmentData.assignmentObjectId),
        ]);
      } catch (fetchError) {
        console.error("Error fetching updated user data:", fetchError);
        throw new Error("Failed to fetch updated user data after assignment.");
      }

      const updatedOptions = updatedUsersData.map((user) => ({
        value: user.userId,
        label: `${user.userName} (${user.email})`,
        userData: user,
      }));

      setUsers(updatedUsersData);
      setUserOptions(updatedOptions);

      const assignedUsersData: AssignmentUser[] = fetchedAssignedUsers.map(
        (user) => ({
          userId: user.userId,
          isActive: true,
        })
      );

      const preSelected = updatedOptions.filter((option) =>
        assignedUsersData.some(
          (assignedUser) => assignedUser.userId === option.value
        )
      );
      setSelectedUsers(preSelected);

      const newInitialUserIds = assignedUsersData.map((user) => user.userId);
      setInitialAssignedUsers(newInitialUserIds);

      setAssignmentData((prev) => ({
        ...prev,
        listOfUsers: assignedUsersData,
      }));

      // Switch to the "Project Details" tab
      setActiveTab("home");
    } catch (error) {
      console.error("Error updating user assignments:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "An error occurred while updating user assignments."
      );
    } finally {
      setIsSubmittingAssignment(false);
      setIsLoadingAssignedUsers(false);
    }
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  if (isLoading || !projectData) {
    return <div className="p-4 text-center">Loading project data...</div>;
  }

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  return (
    <div className="wrapper">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0"></div>
            <div className="card">
              <ul
                className="nav nav-tabs tabHeaderPadding cardHeaderHegiht"
                id="myTab"
                role="tablist"
                style={{ borderBottom: "1px solid #84556b" }}
              >
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link card-title tab-btn cardHeaderHegiht ${
                      activeTab === "home" ? "active" : ""
                    }`}
                    id="home-tab"
                    onClick={() => handleTabClick("home")}
                    type="button"
                    role="tab"
                    aria-controls="home-tab-pane"
                    aria-selected={activeTab === "home"}
                  >
                    Project Details
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link card-title tab-btn cardHeaderHegiht ${
                      activeTab === "files" ? "active" : ""
                    }`}
                    id="files-tab"
                    onClick={() => handleTabClick("files")}
                    type="button"
                    role="tab"
                    aria-controls="files-tab-pane"
                    aria-selected={activeTab === "files"}
                  >
                    Files & Contact Info
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link card-title tab-btn cardHeaderHegiht ${
                      activeTab === "contact" ? "active" : ""
                    }`}
                    id="contact-tab"
                    onClick={() => handleTabClick("contact")}
                    type="button"
                    role="tab"
                    aria-controls="contact-tab-pane"
                    aria-selected={activeTab === "contact"}
                  >
                    Assigners Information
                  </button>
                </li>
              </ul>

              <div className="tab-content card-body" id="myTabContent">
                <div
                  className={`tab-pane fade ${
                    activeTab === "home" ? "show active" : ""
                  }`}
                  id="home-tab-pane"
                  role="tabpanel"
                  aria-labelledby="home-tab"
                  tabIndex={0}
                >
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-12 col-lg-4 mb-3">
                        <label className="form-label">
                          Project Name{requiredField}
                        </label>
                        <input
                          name="projectName"
                          placeholder="Project Name"
                          value={projectData.projectName}
                          onChange={handleChange}
                          required
                          className={`form-control form-control-lg ${
                            errors.projectName ? "is-invalid" : ""
                          }`}
                        />
                        {errors.projectName && (
                          <div className="invalid-feedback text-danger">
                            {errors.projectName}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-4 mb-3">
                        <label className="form-label">
                          Location{requiredField}
                        </label>
                        <select
                          name="locationID"
                          value={projectData.locationID}
                          onChange={handleChange}
                          required
                          className={`form-control form-control-lg ${
                            errors.locationID ? "is-invalid" : ""
                          }`}
                        >
                          <option value="">Select Location</option>
                          {locations.map((location) => (
                            <option
                              key={location.locationID}
                              value={location.locationID}
                            >
                              {location.locationName}
                            </option>
                          ))}
                        </select>
                        {errors.locationID && (
                          <div className="invalid-feedback text-danger">
                            {errors.locationID}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-4 mb-3">
                        <label className="form-label">
                          Description{requiredField}
                        </label>
                        <input
                          name="description"
                          placeholder="Description"
                          value={projectData.description}
                          onChange={handleChange}
                          required
                          className={`form-control form-control-lg ${
                            errors.description ? "is-invalid" : ""
                          }`}
                        />
                        {errors.description && (
                          <div className="invalid-feedback text-danger">
                            {errors.description}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-4 mb-3 mt-3">
                        <label className="form-check">
                          <input
                            type="checkbox"
                            name="isActive"
                            checked={projectData.isActive}
                            onChange={handleChange}
                            className="form-check-input"
                          />
                          <span className="form-check-label">Is Active</span>
                        </label>
                      </div>
                    </div>
                  </form>
                </div>

                <div
                  className={`tab-pane fade ${
                    activeTab === "files" ? "show active" : ""
                  }`}
                  id="files-tab-pane"
                  role="tabpanel"
                  aria-labelledby="files-tab"
                  tabIndex={0}
                >
                  <div className="row">
                    <div className="col-12 col-lg-6 mb-3">
                      <label className="form-label">Brochure</label>
                      <input
                        type="file"
                        className="form-control form-control-lg"
                        onChange={(e) => handleFileChange(e, "brochure")}
                      />
                      {projectData.brochure && !brochureChanged && (
                        <div className="form-text text-success">
                          Brochure file already uploaded
                        </div>
                      )}
                      {brochureFile && (
                        <div className="form-text">
                          Selected: {brochureFile.name}
                        </div>
                      )}
                    </div>

                    <div className="col-12 col-lg-6 mb-3">
                      <label className="form-label">Price Quote</label>
                      <input
                        type="file"
                        className="form-control form-control-lg"
                        onChange={(e) => handleFileChange(e, "priceQuote")}
                      />
                      {projectData.priceQuote && !priceQuoteChanged && (
                        <div className="form-text text-success">
                          Price quote file already uploaded
                        </div>
                      )}
                      {priceQuoteFile && (
                        <div className="form-text">
                          Selected: {priceQuoteFile.name}
                        </div>
                      )}
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">Contact Mails</label>
                      <div
                        className={`form-control form-control-lg ${
                          errors.contactMails ? "is-invalid" : ""
                        }`}
                        style={{
                          height: "auto",
                          minHeight: "60px",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "5px",
                          alignItems: "flex-start",
                          alignContent: "flex-start",
                        }}
                      >
                        {emails.map((email, index) => (
                          <div
                            key={index}
                            className="badge bg-primary p-2 m-1"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              color: "rgb(255 255 255)",
                              backgroundImage:
                                "linear-gradient(90deg, rgba(131,131,232,1) 0%, rgba(0,0,128,1) 100%)",
                              borderRadius: "10px",
                            }}
                          >
                            {email}
                            <span
                              className="ms-2"
                              style={{ cursor: "pointer" }}
                              onClick={() => removeEmail(email)}
                            >
                              ×
                            </span>
                          </div>
                        ))}
                        <input
                          type="email"
                          value={emailInput}
                          onChange={handleEmailInputChange}
                          onKeyDown={handleEmailKeyDown}
                          onBlur={addEmail}
                          placeholder="Type email and press Enter, space or comma"
                          style={{
                            flex: "1",
                            border: "none",
                            outline: "none",
                            minWidth: "150px",
                            background: "transparent",
                          }}
                        />
                      </div>
                      {errors.contactMails && (
                        <div className="text-danger mt-1 small">
                          {errors.contactMails}
                        </div>
                      )}
                      <div className="form-text">
                        Type email and press Enter, space or comma to add
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`tab-pane fade ${
                    activeTab === "contact" ? "show active" : ""
                  }`}
                  id="contact-tab-pane"
                  role="tabpanel"
                  aria-labelledby="contact-tab"
                  tabIndex={0}
                >
                  <form onSubmit={handleAssignmentSubmit}>
                    <div className="row">
                      <div className="col-12 col-lg-6 mb-3">
                        <label className="form-label">
                          Assign Users{requiredField}
                        </label>
                        <Select
                          options={userOptions}
                          value={selectedUsers}
                          onChange={handleMultiSelectChange}
                          isMulti
                          className={`basic-multi-select ${
                            errors.listOfUsers ? "is-invalid" : ""
                          }`}
                          classNamePrefix="select"
                          placeholder="Select users to assign"
                          isLoading={isLoadingUsers}
                          noOptionsMessage={() => "No users available"}
                        />
                        {errors.listOfUsers && (
                          <div className="text-danger mt-1 small">
                            {errors.listOfUsers}
                          </div>
                        )}
                        <div className="form-text">
                          Select multiple users to assign to this project
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                <div>
                  {activeTab !== "contact" ? (
                    <>
                      <button
                        type="submit"
                        className="btn btn-primary me-2"
                        onClick={handleSubmit}
                      >
                        Update Project
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate("/projectView")}
                      >
                        <i className="bi bi-arrow-return-left"></i> Back To List
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary me-2"
                        onClick={handleAssignmentSubmit}
                      >
                        Save Assigner
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate("/projectView")}
                      >
                        <i className="bi bi-arrow-return-left"></i> Back To List
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default EditProject;
